import React, { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Typography,
  Stack,
  Fade,
  ClickAwayListener,
  useMediaQuery,
  useScrollTrigger,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import { useTheme } from "@mui/material/styles";

const sections = [
  { label: "About Me", href: "#about" },
  { label: "Experience", href: "#experience" },
  { label: "Certifications", href: "#certifications" },
  { label: "Tech Stack", href: "#stack" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "Blog / Insights", href: "#blog" },
];

export default function MoreMenu() {
  // 1. ALL HOOKS AT THE TOP LEVEL
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(true);
  const [isExpanded, setIsExpanded] = useState(true);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  
  // MUI Hook to detect scrolling (Pill vs Circle)
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 50 });

  // 2. EFFECT: Sync expansion with scroll
  useEffect(() => {
    setIsExpanded(!scrolled);
  }, [scrolled]);

  // 3. EFFECT: Visibility and Auto-close
  useEffect(() => {
    const handleScroll = () => {
      const heroHeight = window.innerHeight;
      setVisible(window.scrollY < heroHeight - 80);
      if (open && window.scrollY > 50) setOpen(false);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [open]);

  // 4. EFFECT: Swipe Detection
  useEffect(() => {
    if (!isMobile) return;
    let startX = 0;
    let startY = 0;

    const handleTouchStart = (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    };

    const handleTouchEnd = (e) => {
      const dx = e.changedTouches[0].clientX - startX;
      const dy = e.changedTouches[0].clientY - startY;
      if (startX > window.innerWidth - 80 && dx < -50 && Math.abs(dy) < 50) {
        setOpen(true);
      }
    };

    window.addEventListener("touchstart", handleTouchStart);
    window.addEventListener("touchend", handleTouchEnd);
    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isMobile]);

  // 5. HANDLER: Toggle with click-to-expand
  const handleToggle = (e) => {
    e.stopPropagation();
    setOpen((prev) => !prev);
    // If clicked while circle, expand back to pill
    if (!isExpanded) setIsExpanded(true);
  };

  const iconSizes = { xs: "44px", md: "48px" };

  return (
    <Fade in={visible}>
      <Box>
     <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Box sx={{ position: "relative" }}>
            
            <IconButton
              onClick={handleToggle}
              sx={{
                // Shape Logic
                borderRadius: isExpanded ? "14px" : "50%",
                width: isExpanded ? "auto" : iconSizes,
                height: iconSizes,
                px: isExpanded ? 2 : 0,
                
                display: "flex",
                gap: isExpanded ? 1 : 0,
                background: "linear-gradient(135deg, #078d3a, #eed709)",
                color: "#fff",
                boxShadow: "0 8px 25px rgba(0,0,0,0.2)",
                transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                
                "&:hover": { transform: "scale(1.05)" },
              }}
            >
              <MenuIcon fontSize="small" />
              {isExpanded && (
                <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: "1px" }}>
                  MORE
                </Typography>
              )}
            </IconButton>

            {/* PANEL */}
            <Fade in={open}>
              <Box
                sx={{
                  position: "absolute",
                  top: "120%",
                  right: 0,
                  width: 240,
                  p: 2,
                  borderRadius: "16px",
                  background: "rgba(15, 23, 42, 0.95)",
                  backdropFilter: "blur(14px)",
                  border: "1px solid rgba(59,130,246,0.25)",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
                }}
              >
                <Stack spacing={1}>
                  {sections.map((item, i) => (
                    <Box
                      key={i}
                      component="a"
                      href={item.href}
                      onClick={() => setOpen(false)}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        px: 1.5, py: 1, borderRadius: "10px",
                        color: "#e2e8f0", fontWeight: 600, fontSize: "0.85rem",
                        textDecoration: "none",
                        background: "rgba(255,255,255,0.03)",
                        "&:hover": { background: "rgba(255,255,255,0.1)", transform: "translateX(4px)" },
                      }}
                    >
                      {item.label}
                      <ArrowOutwardIcon sx={{ fontSize: 16 }} />
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Fade>
          </Box>
        </ClickAwayListener>
      </Box>
    </Fade>
  );
}
